import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";

/**
 * Basic Ticket Fields
 * Core ticket identification and description fields.
 *
 * Each field is self-contained: display config, role gating, mobile detail
 * section, and (where applicable) `savedViewMembership` derived from
 * `../saved-views/ticket-saved-views.ts` so saved-view definitions can be
 * generated from the field declarations themselves.
 */
export const basicFields = defineFieldConfigMap({
  // FIELD: template
  template: {
    type: "template-select",
    label: "Template",
    placeholder: "Select a ticket template...",
    formOnly: true,
    formSpan: 3,
    templateSelectConfig: {
      templateType: "ticket",
      showClearOption: true,
      fieldMappings: {
        title: "title",
        description: "description",
        tags: "tags",
        priority: "priority",
        status: "status",
      },
    },
    excludeFromApi: true,
    // Table properties
    editable: true,
    hideable: true,
    resizable: true,
    width: 260,
    mobileDetailSectionId: "basic",
  },

  // FIELD: ticket_number
  ticket_number: {
    type: "number",
    label: "Ticket #",
    placeholder: "Auto-generated ticket number",
    required: false,
    // Table properties
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false, // Auto-generated
    filterType: "exact",
    width: 120,
    minWidth: 100,
    maxWidth: 150,
    icon: "Ticket",
    // Role-based access control - visible to all roles (read-only)
    allowedRoles: ["client_user", "wtc_employee", "wtc_admin"],
    showOnCard: true,
    cardSpan: 1,
    mobileDetailSectionId: "basic",
    graphqlSelector: "ticket_number",
    savedViewMembership: [
      "all-tickets",
      "my-tickets",
      "high-priority",
      "open-tickets",
      "overdue-tickets",
      "resolved-tickets",
      "no-time-entries",
      "with-time-entries",
    ],
  },

  // FIELD: title
  title: {
    type: "text",
    label: "Title",
    placeholder: "What is the issue?",
    required: true,
    formSpan: 3,
    validation: {
      required: "Ticket title is required.",
    },
    // Table properties
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "contains",
    width: 300,
    minWidth: 200,
    maxWidth: 500,
    icon: "FileText",
    // Clients must see their own ticket's title (it's the subject they submitted).
    allowedRoles: ["client_user", "wtc_employee", "wtc_admin"],
    // Show row actions inline with this column
    showRowActions: true,
    showOnCard: true,
    cardSpan: 2,
    mobileDetailSectionId: "basic",
    graphqlSelector: "title",
    savedViewMembership: [
      "all-tickets",
      "my-tickets",
      "high-priority",
      "open-tickets",
      "overdue-tickets",
      "resolved-tickets",
      "no-time-entries",
      "with-time-entries",
    ],
  },

  // FIELD: description
  description: {
    type: "textarea",
    label: "Description",
    placeholder: "Describe the issue in detail...",
    required: true,
    formSpan: 3,
    validation: {
      required: "Ticket description is required.",
    },
    // Table properties
    sortable: false,
    filterable: false,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    width: 250,
    minWidth: 150,
    maxWidth: 400,
    icon: "FileText",
    // Clients see the description of their own ticket (they wrote it).
    allowedRoles: ["client_user", "wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "basic",
    graphqlSelector: "description",
  },

  // FIELD: contact_id
  contact_id: {
    type: "client-select",
    label: "Contact",
    isForeignKey: true,
    foreignKey: {
      referencedTable: "contacts",
      referencedColumn: "id",
      constraintName: "tickets_contact_id_fkey",
      onDelete: "CASCADE",
      onUpdate: "NO ACTION",
      relationKind: "many-to-one",
    },
    placeholder: "Search for contact...",
    required: true,
    formSpan: 3,
    validation: {
      required: "A contact must be selected.",
    },
    clientSelectConfig: {
      showModal: true,
      limit: 20,
    },
    // Table properties
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "client-select",
    width: 200,
    minWidth: 150,
    maxWidth: 300,
    icon: "User",
    groupable: true,
    // Role-based access control
    allowedRoles: ["wtc_employee", "wtc_admin"],
    showOnCard: true,
    cardSpan: 3,
    mobileDetailSectionId: "basic",
  },

  // FIELD: company_id
  company_id: {
    type: "text",
    label: "Company",
    isForeignKey: true,
    foreignKey: {
      referencedTable: "companies",
      referencedColumn: "id",
      constraintName: "tickets_company_id_fkey",
      onDelete: "SET NULL",
      onUpdate: "NO ACTION",
      relationKind: "many-to-one",
    },
    placeholder: "Auto-populated from contact",
    required: false,
    formOnly: true,
    excludeFromApi: true, // ✅ Exclude from API submission
    // Table properties
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    filterType: "company-select",
    width: 200,
    minWidth: 150,
    maxWidth: 300,
    icon: "Building2",
    mobileDetailSectionId: "basic",
  },
});
